import { useEffect, useRef } from 'react';
import { useAppSelector } from '../../store';
import { ACTION_QUEUE_CHANGED_EVENT, processActionQueue } from '../../lib/actionQueueProcessor';

const SYNC_INTERVAL_MS = 15000;

/**
 * Background worker: replays queued API actions when online.
 * Keeps Command and MDT workflows moving after transient failures.
 */
export default function ActionQueueProcessor() {
  const { isAuthenticated, token } = useAppSelector((state) => state.auth);
  const syncingRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const runSync = async () => {
      if (syncingRef.current || !navigator.onLine) return;
      syncingRef.current = true;
      try {
        await processActionQueue();
      } finally {
        syncingRef.current = false;
      }
    };

    void runSync();

    const onOnline = () => void runSync();
    window.addEventListener('online', onOnline);

    const interval = window.setInterval(() => void runSync(), SYNC_INTERVAL_MS);

    return () => {
      window.removeEventListener('online', onOnline);
      window.clearInterval(interval);
    };
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const onQueueChanged = () => {
      if (navigator.onLine) {
        void processActionQueue();
      }
    };

    window.addEventListener(ACTION_QUEUE_CHANGED_EVENT, onQueueChanged);
    return () => window.removeEventListener(ACTION_QUEUE_CHANGED_EVENT, onQueueChanged);
  }, [isAuthenticated, token]);

  return null;
}
